import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible, fieldWritable, type Me } from '../auth/me';
import { FINANCE_KEY } from '../finance/types';
import { INVOICES_KEY } from '../invoices/types';
import { PAYMENTS_KEY } from '../payments/types';
import { refreshQueries } from '../refresh';
import { ConflictNotice } from '../ui/ConflictNotice';
import { EVENTS_KEY, EVENTS_RESOURCE, SETTLEMENT_LABELS, type EventView, type Settlement } from './labels';

/**
 * The event's „Rozliczenie” shown and changed outside its form (owner 2026-11-04: Finanse → Podsumowanie). It saves
 * the same way as the form — PATCH of the event with its version — so the server's rules hold: installments follow,
 * „Bez faktury” is refused once the event has an invoice (its Polish reason shown), a newer version ends in a conflict.
 * Rule 7a: nothing without the field; plain text without the right to edit events.
 */
export function SettlementPicker({ me, eventId, eventName, version, value }: { me: Me; eventId: string; eventName: string; version: number | undefined; value: Settlement | undefined }) {
  const queryClient = useQueryClient();
  const refresh = () => Promise.all([EVENTS_KEY, FINANCE_KEY, PAYMENTS_KEY, INVOICES_KEY].map((key) => refreshQueries(queryClient, key)));
  const save = useMutation({
    mutationFn: (settlement: Settlement) => api<EventView>(`/api/events/${eventId}`, { method: 'PATCH', body: { version, settlement } }),
    onSuccess: refresh,
  });
  if (!fieldVisible(me, EVENTS_RESOURCE, 'settlement') || value === undefined) return null;
  if (!can(me, EVENTS_RESOURCE, 'edit') || !fieldWritable(me, EVENTS_RESOURCE, 'settlement') || version === undefined) return <>{SETTLEMENT_LABELS[value]}</>;
  const error = save.error instanceof ApiError ? save.error : null;
  const conflict = error?.status === 409;
  return (
    <div className="settlement-picker" data-feature-id="events.settlement">
      <select
        aria-label={`Rozliczenie: ${eventName}`}
        value={save.isPending ? save.variables : value}
        disabled={save.isPending}
        onChange={(e) => save.mutate(e.target.value as Settlement)}
      >
        {(Object.keys(SETTLEMENT_LABELS) as Settlement[]).map((s) => (
          <option key={s} value={s}>
            {SETTLEMENT_LABELS[s]}
          </option>
        ))}
      </select>
      {conflict && (
        <ConflictNotice
          onReload={() => {
            save.reset();
            void refresh();
          }}
        />
      )}
      {save.isError && !conflict && (
        <p className="alert" role="alert">
          {error ? (fieldIssues(error)[0]?.message ?? error.message) : 'Nie udało się zmienić rozliczenia'}
        </p>
      )}
    </div>
  );
}
